import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@lf/ui-primitives';
import { getCoppaConsentRequest, grantCoppaConsent } from '../api/lfApiChildLink';

function ParentConsentPage() {
    const { token } = useParams();
    const [consent, setConsent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [checked, setChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [granted, setGranted] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const resp = await getCoppaConsentRequest(token);
            if (cancelled) return;
            if (resp && resp.data && resp.data.consent) {
                setConsent(resp.data.consent);
                if (resp.data.consent.status === 'granted') {
                    setGranted(true);
                }
            } else {
                setError('This consent link is invalid or has expired.');
            }
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [token]);

    async function handleGrant(e) {
        if (e) e.preventDefault();
        setError('');
        if (!checked) {
            setError('Please tick the checkbox to confirm you grant permission.');
            return;
        }
        setSubmitting(true);
        const resp = await grantCoppaConsent(token, true);
        setSubmitting(false);
        if (resp && resp.data && resp.data.success) {
            setGranted(true);
        } else {
            setError('Could not record your consent. The link may have been replaced or expired.');
        }
    }

    if (loading) {
        return <div className="text-center mt-12">Loading...</div>;
    }

    if (error && !consent) {
        return (
            <div className="max-w-md mx-auto mt-12 border border-gray-300 bg-white rounded-2xl p-6 text-left">
                <h2 className="text-xl font-semibold mb-2">Link unavailable</h2>
                <p className="text-sm text-gray-700">{error}</p>
            </div>
        );
    }

    if (granted) {
        return (
            <div className="max-w-md mx-auto mt-12 border border-gray-300 bg-white rounded-2xl p-6 text-left">
                <h2 className="text-xl font-semibold mb-2">Thank you!</h2>
                <p className="text-sm text-gray-700">
                    You&apos;ve granted permission for <strong>{consent.child_first_name}</strong> to
                    use Learnforest. We&apos;ve emailed you a confirmation copy. You may
                    revoke this permission at any time by signing in to the{' '}
                    <a href="/" className="text-blue-500 underline">parent dashboard</a>{' '}
                    at Learnforest using the same email address.
                </p>
            </div>
        );
    }

    const grantDisabled = !checked || submitting;

    return (
        <div className="max-w-2xl mx-auto my-8 border border-gray-300 bg-white rounded-2xl p-6 text-left">
            <h1 className="text-2xl font-semibold mb-2">
                Approve {consent.child_first_name} for Learnforest
            </h1>
            <p className="text-sm text-gray-700 mb-4">
                <strong>{consent.child_first_name}</strong> listed this email
                (<strong>{consent.parent_email}</strong>) as their parent or
                guardian. Please review the notice below before approving.
            </p>

            <div className="text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-line mb-4 text-left">
                {consent.notice_text}
            </div>

            <form onSubmit={handleGrant}>
                <label className="flex items-start gap-2 text-sm text-gray-800 mb-3">
                    <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        onChange={e => setChecked(e.target.checked)}
                    />
                    <span>
                        I am the parent or legal guardian of{' '}
                        <strong>{consent.child_first_name}</strong>, and I grant
                        Learnforest permission to collect and use the personal
                        information described above.
                    </span>
                </label>

                {error && <div className="text-red-500 text-sm mb-2">{error}</div>}

                <LoadingButton
                    type="submit"
                    className={`${grantDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'} text-white py-2 px-4 rounded`}
                    text="Grant permission"
                    isLoading={submitting}
                    disabled={grantDisabled}
                    onClick={handleGrant}
                />
            </form>

            <p className="text-xs text-gray-500 mt-6">
                Don&apos;t recognize this request? You can safely close this page —
                the account will remain blocked until permission is granted.
            </p>
        </div>
    );
}

export default ParentConsentPage;
